import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { sessionGet } from '@/utils/sessionStore';
import {
  LayoutDashboard,
  TrendingUp,
  CreditCard,
  ArrowRightLeft,
  Building2,
  Receipt,
  DollarSign,
  FileText,
  Link2,
  BarChart3,
  Calculator,
  Presentation,
  Lightbulb,
  ScrollText,
  Sparkles,
  Settings,
  FolderOpen,
  Tags,
  FileSpreadsheet,
  LogOut,
  ChevronDown,
  ChevronRight,
  Building,
  Sun,
  Moon,
  BookUser,
  Scale,
  Users,
  Shield,
  CalendarClock,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';

// â”€â”€â”€ Theme tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Tema "Pulse" (2026-07) — acento indigo fintech (#635BFF), ver mockups/propuesta_ux_fintech.html
const DARK = {
  bg:        '#0B1020',
  sidebar:   '#0E1428',
  sidebar2:  '#171F38',
  border:    'rgba(255,255,255,0.07)',
  border2:   'rgba(255,255,255,0.13)',
  text1:     '#F0F2F8',
  text2:     '#93A0BC',
  text3:     '#566180',
  active:    'rgba(124,117,255,0.14)',
  activeBar: '#7C75FF',
  accent:    '#7C75FF',
  accentTxt: '#9B95FF',
  hover:     'rgba(255,255,255,0.07)',
  hoverText: '#CBD5E1',
  main:      '#0B1020',
};

const LIGHT = {
  bg:        '#F7F8FA',
  sidebar:   '#FFFFFF',
  sidebar2:  '#F3F4F9',
  border:    'rgba(15,23,42,0.07)',
  border2:   'rgba(15,23,42,0.13)',
  text1:     '#0F172A',
  text2:     '#475569',
  text3:     '#94A3B8',
  active:    'rgba(99,91,255,0.10)',
  activeBar: '#635BFF',
  accent:    '#635BFF',
  accentTxt: '#4B44D6',
  hover:     'rgba(15,23,42,0.05)',
  hoverText: '#0F172A',
  main:      '#F7F8FA',
};

// â”€â”€â”€ Nav structure â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const buildNav = (isAdmin, isCFO) => {
  if (isAdmin) {
    return [{
      section: '',
      items: [{ name: 'Panel Admin', href: '/admin', icon: Shield, badge: 'ADMIN', badgeStyle: 'admin' }],
    }];
  }
  return [
  {
    section: 'Principal',
    items: [
      { name: 'Dashboard',        href: '/',             icon: LayoutDashboard },
      { name: 'Cash Flow',        href: '/projections',  icon: TrendingUp },
      { name: 'Cobranza y Pagos', href: '/payments',     icon: CreditCard },
      { name: 'Aging CxC / CxP', href: '/transactions', icon: ArrowRightLeft },
    ],
  },
  {
    section: 'Operaciones',
    items: [
      {
        name: 'Cuentas y Bancos',
        icon: Building2,
        sub: [
          { name: 'Cuentas bancarias',        href: '/bank' },
          { name: 'Conciliaciones',            href: '/bank-statements' },
          { name: 'Importar estado de cuenta', href: '/bank-statements?import=true' },
          { name: 'Tipos de cambio',           href: '/fx-rates' },
        ],
      },
    ],
  },
  {
    section: 'CFO Digital',
    items: [
      {
        name: 'Reportes',
        icon: BarChart3,
        sub: [
          { name: 'Reporte Board',        href: '/board-report' },
          { name: 'Métricas financieras', href: '/financial-metrics' },
          { name: 'Decisiones / Alertas', href: '/treasury' },
          { name: 'Cash Flow Analítico',  href: '/reports' },
        ],
      },
      {
        name: 'IA Ejecutiva',
        href: '/advanced',
        icon: Sparkles,
        badge: 'PRO',
        badgeStyle: 'pro',
      },
      {
        name: 'Consejo Estratégico',
        href: '/consejo-estrategico',
        icon: Scale,
        badge: 'IA',
        badgeStyle: 'pro',
      },
    ],
  },
  {
    section: 'Financiamientos',
    items: [
      { name: 'Simulador de Crédito', href: '/financiamiento', icon: DollarSign },
    ],
  },
  {
    section: 'Fiscal',
    items: [
      {
        name: 'Planeación Fiscal',
        href: '/planeacion-fiscal',
        icon: CalendarClock,
        badge: 'NUEVO',
        badgeStyle: 'pro',
      },
      {
        name: 'SAT y Fiscal',
        icon: FileText,
        badge: 'MX',
        badgeStyle: 'mx',
        sub: [
          { name: 'CFDI',                               href: '/cfdi' },
          { name: 'Integraciones (Alegra · Contalink)', href: '/integrations' },
          { name: 'Estados Financieros',                href: '/contalink-financial' },
        ],
      },
      { name: 'Portal Auditoría', href: '/audit', icon: FolderOpen },
      { name: 'Catálogo', href: '/catalogs', icon: BookUser },
    ],
  },
  ...(isCFO
    ? [{
        section: 'Equipo',
        items: [{ name: 'Usuarios', href: '/usuarios', icon: Users }],
      }]
    : []),
  ];
};

// â”€â”€â”€ Badge styles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BADGE = {
  mx:    { bg: 'rgba(96,165,250,0.12)',  color: '#60A5FA' },
  pro:   { bg: 'rgba(167,139,250,0.12)', color: '#A78BFA' },
  v2:    { bg: 'rgba(255,179,71,0.12)',  color: '#FFB347' },
  admin: { bg: 'rgba(239,68,68,0.12)',   color: '#EF4444' },
};

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const Layout = ({ user, onLogout, companies, selectedCompany, onCompanyChange }) => {
  const location = useLocation();
  const storedUser = JSON.parse(sessionGet('user') || '{}');
  const PLATFORM_ADMIN_EMAIL = 'hola@taxnfin.com';
  const isAdmin = storedUser?.role === 'admin' && storedUser?.email === PLATFORM_ADMIN_EMAIL;
  const isCFO = storedUser?.role === 'cfo' || storedUser?.role === 'admin';
  const navigate = useNavigate();

  // Theme
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('taxnfin-theme');
    return saved ? saved === 'dark' : true;
  });
  const t = dark ? DARK : LIGHT;

  useEffect(() => {
    localStorage.setItem('taxnfin-theme', dark ? 'dark' : 'light');
  }, [dark]);

  // Platform admin cannot access financial routes — redirect to /admin
  useEffect(() => {
    if (isAdmin && location.pathname !== '/admin') {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, location.pathname, navigate]);

  // Open sections (collapsible groups)
  const nav = buildNav(isAdmin, isCFO);

  const getDefaultOpen = () => {
    const open = {};
    nav.forEach(({ items }) => {
      items.forEach((item) => {
        if (item.sub) {
          const anyActive = item.sub.some(
            (s) => location.pathname === s.href || location.pathname + location.search === s.href
          );
          if (anyActive) open[item.name] = true;
        }
      });
    });
    return open;
  };

  const [openGroups, setOpenGroups] = useState(getDefaultOpen);

  const toggleGroup = (name) =>
    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));

  const isActive = (href) =>
    href && (location.pathname === href || location.pathname + location.search === href);

  const isGroupActive = (item) =>
    item.href
      ? isActive(item.href)
      : item.sub?.some((s) => isActive(s.href));

  // â”€â”€ Styles (inline, theme-aware) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const S = {
    shell: {
      display: 'flex', height: '100vh',
      background: t.main, fontFamily: "'Figtree', -apple-system, sans-serif",
    },
    sidebar: {
      width: 240, minWidth: 240,
      background: t.sidebar,
      borderRight: `1px solid ${t.border}`,
      display: 'flex', flexDirection: 'column',
      transition: 'background 0.25s, border-color 0.25s',
    },
    header: { padding: '16px 16px 14px', borderBottom: `1px solid ${t.border}` },
    logoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
    logoMark: {
      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
      background: 'linear-gradient(135deg,#635BFF,#4B44D6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Figtree', sans-serif", fontSize: 13, fontWeight: 800, color: '#fff',
    },
    logoName: { fontSize: 14, fontWeight: 700, color: t.text1, letterSpacing: -0.3, marginLeft: 8, flex: 1 },
    logoSub:  { fontSize: 9, color: t.accent, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginLeft: 8 },
    nav: { flex: 1, padding: '10px 8px 6px', overflowY: 'auto' },
    // Tema "Pulse" (fase 3): items tipo píldora como el mockup — más aire,
    // tipografía más grande, activo con fondo indigo suave sin barra lateral.
    sectionLabel: {
      fontSize: 10, fontWeight: 700, color: t.text3,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      padding: '14px 10px 5px',
    },
    navItem: (active) => ({
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 11px', borderRadius: 9, cursor: 'pointer',
      transition: 'background 0.12s, color 0.12s',
      color: active ? t.accentTxt : t.text2,
      background: active ? t.active : 'transparent',
      position: 'relative', marginBottom: 2,
      textDecoration: 'none',
    }),
    activeBar: {
      display: 'none',   // el mockup usa píldora completa, sin barra lateral
    },
    navLabel: (active) => ({
      fontSize: 13.5, flex: 1, lineHeight: 1.1,
      fontWeight: active ? 600 : 500, color: 'inherit',
    }),
    subItem: (active) => ({
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 10px 6px 37px', fontSize: 12.5,
      color: active ? t.accentTxt : t.text3,
      borderRadius: 7, cursor: 'pointer', marginBottom: 1,
      transition: 'color 0.12s, background 0.12s',
      background: active ? t.active : 'transparent',
      textDecoration: 'none',
      fontWeight: active ? 600 : 400,
    }),
    footer: { padding: '8px 8px 12px', borderTop: `1px solid ${t.border}` },
    footItem: {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 9px', fontSize: 11.5, color: t.text3,
      borderRadius: 7, cursor: 'pointer',
      transition: 'color 0.12s, background 0.12s',
      textDecoration: 'none',
    },
    themeRow: {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 9px', borderRadius: 7, cursor: 'pointer',
      fontSize: 11.5, color: t.text3, marginBottom: 2,
      transition: 'background 0.12s',
    },
    main: { flex: 1, overflowY: 'auto', background: t.main, transition: 'background 0.25s' },
  };

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <div style={S.shell} data-testid="main-layout">

      {/* â”€â”€ SIDEBAR â”€â”€ */}
      <aside style={S.sidebar} data-testid="sidebar">

        {/* Header */}
        <div style={S.header}>
          <div style={S.logoRow}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/taxnfin-logo.svg" alt="TaxnFin" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
              <div>
                <div style={S.logoName}>TaxnFin</div>
                <div style={S.logoSub}>Cashflow</div>
              </div>
            </div>
            <NotificationBell />
          </div>

          {/* Company selector */}
          <div style={{ fontSize: 9, fontWeight: 700, color: t.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>
            Empresa activa
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="company-selector"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 7,
                  background: t.sidebar2, border: `1px solid ${t.border2}`,
                  borderRadius: 8, padding: '7px 10px', cursor: 'pointer',
                  color: t.text1, fontSize: 13, fontWeight: 600,
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <Building size={14} color={t.accent} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedCompany?.nombre || 'Seleccionar empresa'}
                </span>
                <ChevronDown size={13} color={t.text3} style={{ flexShrink: 0 }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start">
              {companies.length === 0 ? (
                <DropdownMenuItem disabled>No hay empresas disponibles</DropdownMenuItem>
              ) : (
                companies.map((company) => (
                  <DropdownMenuItem
                    key={company.id}
                    onClick={() => onCompanyChange(company)}
                    className={cn('cursor-pointer', selectedCompany?.id === company.id && 'bg-[#F1F5F9]')}
                    data-testid={`company-option-${company.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{company.nombre}</span>
                      <span className="text-xs text-[#64748B]">RFC: {company.rfc}</span>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Nav */}
        <nav style={S.nav}>
          {nav.map(({ section, items }, sectionIdx) => (
            <div key={section} style={{
              marginBottom: 4,
              ...(sectionIdx > 0 && section ? {
                borderTop: `1px solid ${t.border}`,
                marginTop: 10,
                paddingTop: 6,
              } : {}),
            }}>
              {section && <div style={S.sectionLabel}>{section}</div>}

              {items.map((item) => {
                const Icon = item.icon;
                const active = isGroupActive(item);
                const isOpen = !!openGroups[item.name];

                // Simple link (no sub)
                if (item.href && !item.sub) {
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      style={S.navItem(active)}
                      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = t.hover; e.currentTarget.style.color = t.hoverText; } }}
                      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.text2; } }}
                    >
                      {active && <div style={S.activeBar} />}
                      <Icon size={15} style={{ flexShrink: 0 }} />
                      <span style={S.navLabel(active)}>{item.name}</span>
                      {item.badge && (
                        <span style={{ fontSize: 9, fontWeight: 700, borderRadius: 3, padding: '1px 5px', ...BADGE[item.badgeStyle] }}>
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                }

                // Collapsible group
                return (
                  <div key={item.name}>
                    <div
                      style={S.navItem(active)}
                      onClick={() => toggleGroup(item.name)}
                      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = t.hover; e.currentTarget.style.color = t.hoverText; } }}
                      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.text2; } }}
                    >
                      {active && <div style={S.activeBar} />}
                      <Icon size={15} style={{ flexShrink: 0 }} />
                      <span style={S.navLabel(active)}>{item.name}</span>
                      {item.badge && (
                        <span style={{ fontSize: 9, fontWeight: 700, borderRadius: 3, padding: '1px 5px', ...BADGE[item.badgeStyle] }}>
                          {item.badge}
                        </span>
                      )}
                      {isOpen
                        ? <ChevronDown size={12} color={t.text3} style={{ flexShrink: 0 }} />
                        : <ChevronRight size={12} color={t.text3} style={{ flexShrink: 0 }} />}
                    </div>

                    {/* Sub items */}
                    <div style={{
                      overflow: 'hidden',
                      maxHeight: isOpen ? 300 : 0,
                      transition: 'max-height 0.22s ease',
                    }}>
                      {item.sub.map((s) => {
                        const subActive = isActive(s.href);
                        return (
                          <Link
                            key={s.name}
                            to={s.href}
                            style={S.subItem(subActive)}
                            onMouseEnter={(e) => { if (!subActive) { e.currentTarget.style.background = t.hover; e.currentTarget.style.color = t.hoverText; } }}
                            onMouseLeave={(e) => { if (!subActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.text3; } }}
                          >
                            <span style={{ width: 4, height: 4, borderRadius: '50%', background: subActive ? t.accentTxt : t.text3, flexShrink: 0 }} />
                            <span style={{ flex: 1, color: 'inherit' }}>{s.name}</span>
                            {s.tag && (
                              <span style={{ fontSize: 8.5, fontWeight: 600, borderRadius: 3, padding: '1px 4px', ...BADGE.v2 }}>
                                {s.tag}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div style={S.footer}>
          {/* User info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', marginBottom: 2 }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,#00C9A7,#008B72)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: '#fff',
            }}>
              {user?.nombre?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: t.text1 }}>{user?.nombre}</div>
              <div style={{ fontSize: 10, color: t.text3 }}>{user?.role} Â· MXN</div>
            </div>
          </div>

          {/* Theme toggle */}
          <div style={S.themeRow} onClick={() => setDark((d) => !d)}
            onMouseEnter={(e) => e.currentTarget.style.background = t.hover}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            {dark
              ? <Sun size={14} color={t.text3} />
              : <Moon size={14} color={t.text3} />}
            <span style={{ flex: 1, color: t.text3 }}>{dark ? 'Modo diurno' : 'Modo nocturno'}</span>
            {/* Track */}
            <div style={{
              width: 28, height: 16, borderRadius: 8, flexShrink: 0, position: 'relative',
              background: dark ? t.border2 : t.accent,
              transition: 'background 0.25s',
            }}>
              <div style={{
                position: 'absolute', top: 2, left: 2,
                width: 12, height: 12, borderRadius: '50%', background: '#fff',
                transition: 'transform 0.22s',
                transform: dark ? 'translateX(0)' : 'translateX(12px)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
              }} />
            </div>
          </div>

          {/* Config links */}
          <Link to="/categories" style={S.footItem}
            onMouseEnter={(e) => e.currentTarget.style.background = t.hover}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Tags size={13} color={t.text3} />
            <span>CategorÃ­as &amp; CatÃ¡logos</span>
          </Link>

          {isAdmin && (
            <Link to="/admin" style={S.footItem}
              onMouseEnter={(e) => e.currentTarget.style.background = t.hover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Settings size={13} color={t.text3} />
              <span>Admin</span>
            </Link>
          )}

          {isAdmin && (
            <Link to="/audit-logs" style={S.footItem}
              onMouseEnter={(e) => e.currentTarget.style.background = t.hover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ScrollText size={13} color={t.text3} />
              <span>BitÃ¡cora</span>
            </Link>
          )}

          {/* Logout */}
          <div
            style={{ ...S.footItem, color: '#EF4444', marginTop: 2 }}
            onClick={onLogout}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <LogOut size={13} />
            <span>Cerrar sesiÃ³n</span>
          </div>
        </div>
      </aside>

      {/* â”€â”€ MAIN â”€â”€ */}
      <main style={S.main}>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
